import { Telemetry } from '@/hooks/useTelemetry';
import { whenTelemetryReady } from '@/services/telemetry';

/**
 * How long the crash UI must stay mounted before we emit
 * `CriticalApplicationError`. The Slack Oncall alert counts those exceptions
 * with no delay, so sending one at the moment the boundary catches would page
 * even when an auto-reload then navigates the tab away and the next load
 * recovers. 10s is long enough for `location.reload()` to unload this
 * document (that cancels the timer) and short enough that a student left on
 * "Oops, there is an error!" still pages on the alert's next evaluation.
 */
export const CRASH_CONFIRM_MS = 10_000;

const AUTO_RELOAD_PENDING_KEY = 'chunkLoadAutoReloadPending';

/**
 * Set just before the chunk-load auto-reload, so the next document can tell
 * a recovered reload from a fresh visit. sessionStorage can throw (privacy
 * mode); a missing mark means we simply won't emit the recovery event.
 */
export function markAutoReloadPending(): void {
  try {
    sessionStorage.setItem(AUTO_RELOAD_PENDING_KEY, '1');
  } catch {
    // best-effort signal
  }
}

/**
 * True once per pending auto-reload. Clears the mark as a side effect so a
 * later manual refresh is not reported as another recovery.
 */
export function consumeAutoReloadPending(): boolean {
  try {
    if (sessionStorage.getItem(AUTO_RELOAD_PENDING_KEY) !== '1') return false;
    sessionStorage.removeItem(AUTO_RELOAD_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Call once the lazy lesson chunk has actually loaded. If this document
 * exists because the previous one auto-reloaded, record that the student
 * came back — and do not leave the mark around for a later crash to misread.
 * Never throws: a failure here must not become a new crash.
 */
export function noteAutoReloadRecovered(): void {
  if (!consumeAutoReloadPending()) return;
  try {
    whenTelemetryReady(() => {
      try {
        Telemetry()?.addPageAction('app_auto_reload_recovered');
      } catch {
        // telemetry must not break the recovered page
      }
    });
  } catch {
    // telemetry must not break the recovered page
  }
}
